import React, { useCallback, useEffect } from "react";
import { Flex, TextField, Text } from "@aws-amplify/ui-react";
import ParentSize from '@visx/responsive/lib/components/ParentSize';
import GraphSetting from "./GraphSetting";
import {
  downloadSettingFile,
  getMaskSettingJson,
  updateMaskSettingJson,
} from '../../../lib/SettingUtil';
import {
  CheckWrapper,
  CommonBtnRoundWrapper,
} from '../../../componentsWrapper';
import { CustomModal } from "../../CustomModal";
import { getFormatNowDate } from '../../../lib/StorageUtil';
import { WAVE_MONITERING_BAND_LIST, getNearestValue } from "../../../lib/ConstList";

// ローカルストレージのキー
const LOCAL_STORAGE_KEY = "mask-setting-trace-data";

// 干渉波マスク設定の上限
const MAX_MASK_SETTING = 100;

const InterferenceMonitoringMaskSettings = ({
  location,
  openChangeModal,
  onCloseChangeModal,
  displaySettingJson,
  downloadCount,
  inputSettingJson,
}) => {
  const [traceData, setTraceData] = React.useState({
    "max": [],
    "20%": [],
    "ave": [],
  });

  // マスク設定
  const [maskSetting, setMaskSetting] = React.useState([]);

  // 編集中のマスク設定
  const [editMaskSetting, setEditMaskSetting] = React.useState([]);

  // 現在選択されているマスク設定
  const [selectedMaskSetting, setSelectedMaskSetting] = React.useState(null);

  // マスク設定の操作状態
  const [maskSettingStatus, setMaskSettingStatus] = React.useState({
    event: null,
    mask: null,
  });

  // traceデータを読み込むinput
  const traceDataInputRef = React.useRef(null);

  // ファイルを読み込む
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (e) => {
        const csvData = e.target.result;

        initTraceData(csvData);
      };
      reader.readAsText(file);
    }
  }

  // UIコンポーネントの都合上、GUNMAが来るのでSAYADOに変換する関数
  const convertLocationName = useCallback((locationName) => {
    return locationName === "GUNMA" ? "SAYADO" : locationName;
  }, []);

  // CSVデータを解析して初期化
  const initTraceData = (csvData) => {
    const lines = csvData.split('\n');
    console.warn("initTraceData", lines);
    
    const data = {
      "label": [],
      "max": [],
      "20%": [],
      "ave": [],
    }

    for (const line of lines) {
      let [_, traceType, dataType, ...csvData] = line.split(',').map(item => item.trim());

      if (traceType === "trace type"
        && dataType === "data type"
      ) {
        data.label = csvData.map(Number);
      }

      if (traceType === "MaxHold" &&
        dataType === "最大値") {
        data.max = data.label.map((key, index) => {
          return {
            x: key,
            y: Number(csvData[index]),
          }
        })
      }
      if (traceType === "MaxHold" &&
        dataType === "時間率20%") {
        data["20%"] = data.label.map((key, index) => {
          return {
            x: key,
            y: Number(csvData[index]),
          }
        })
      }
      if (traceType === "Average" &&
        dataType === "最大値") {
        data.ave = data.label.map((key, index) => {
          return {
            x: key,
            y: Number(csvData[index]),
          }
        })
      }
    }

    // データが正しく取得できているか確認
    if (data.max.length === 0 || data["20%"].length === 0 || data.ave.length === 0) {
      alert("traceデータの読み込みに失敗しました。\n正しい形式のtraceデータを選択してください。");
      return;
    }

    setTraceData({
      "max": data.max,
      "20%": data["20%"],
      "ave": data.ave,
    });

    // ローカルストレージに保存
    localStorage.setItem(LOCAL_STORAGE_KEY, JSON.stringify(data));
  }

  // マスク設定の状態を更新する関数
  const onMaskStatusChange = (status) => {
    setMaskSettingStatus(status);
  }

  // マスク設定を取得する関数
  const getMaskSetting = async () => {
    const setting = await getMaskSettingJson(convertLocationName(location));

    if (!setting) {
      console.log("マスク設定が取得できませんでした。");
      return;
    }

    // idを追加
    const settingWithId = setting.map((item, index) => ({
      ...item,
      id: crypto.randomUUID(),
    }));

    console.warn("getMaskSetting", settingWithId);

    setMaskSetting(settingWithId);
    setEditMaskSetting(settingWithId);
  }

  // 初期化処理
  const init = () => {
    // 初期化
    setMaskSettingStatus({
      event: null,
      mask: null,
    });
    setEditMaskSetting([]);
    setTraceData({
      "max": [],
      "20%": [],
      "ave": [],
    });
    setMaskSetting([]);

    // マスク設定を取得
    getMaskSetting();

    // ローカルストレージからデータを取得
    const data = localStorage.getItem(LOCAL_STORAGE_KEY);
    if (!data) {
      return;
    }

    try {
      const parsedData = JSON.parse(data);
      setTraceData(parsedData);
    } catch (error) {
      console.log("Error parsing data from localStorage:", error);

      setTraceData({
        "max": [],
        "20%": [],
        "ave": [],
      });
    }
  }

  // 保存ボタンがクリックされたときの処理
  const onClickSaveButton = async () => {
    console.log("onClickSaveButton", editMaskSetting);

    // idを削除し、バリデーションを行う
    let saveSettingValueJson = editMaskSetting.map((item) => {
      const { start, end } = item;

      // startとendが同じ場合は削除
      if (start === end) {
        return null;
      }

      // startとendが逆の場合は入れ替え
      return {
        start: Math.min(start, end),
        end: Math.max(start, end),
      }
    });

    // nullを削除
    saveSettingValueJson = saveSettingValueJson.filter((item) => item !== null);

    // モーダルを閉じる
    onCloseChangeModal();

    // 設定更新
    await updateMaskSettingJson(convertLocationName(location), saveSettingValueJson);

    // 設定値を再取得
    getMaskSetting();
  }

  const onBlurMaskSetting = () => {
    const mask = {
      ...selectedMaskSetting,
      start: Math.min(selectedMaskSetting.start, selectedMaskSetting.end),
      end: Math.max(selectedMaskSetting.start, selectedMaskSetting.end),
    }

    // 範囲外に出る場合
    if (mask.start < WAVE_MONITERING_BAND_LIST.min) {
      mask.start = WAVE_MONITERING_BAND_LIST.min;
    } else if (mask.end > WAVE_MONITERING_BAND_LIST.max) {
      mask.end = WAVE_MONITERING_BAND_LIST.max;
    }

    // 近い値に丸める
    mask.start = getNearestValue(mask.start);
    mask.end = getNearestValue(mask.end);

    setSelectedMaskSetting(mask);

    setEditMaskSetting((prev) => {
      return prev.map((prevMask) => {
        if (prevMask.id === selectedMaskSetting.id) {
          return mask;
        }
        return prevMask;
      });
    });
  }

  useEffect(() => {
    init();
  }, [location]);

  // ダウンロードボタンイベント
  useEffect(() => {
    if (downloadCount > 0) {
      const locationName = displaySettingJson.location_name[convertLocationName(location)];
      const fileName = `interference-monitoring-干渉波マスク設定-${locationName}-${getFormatNowDate()}.json`;

      // マスク設定にはidが含まれるので、idを削除してダウンロード
      const downloadMaskSetting = editMaskSetting.map((item) => {
        const { id, ...rest } = item;
        return rest;
      });

      // マスク設定をダウンロード
      downloadSettingFile(downloadMaskSetting, fileName);
    }
  }, [downloadCount]);

  useEffect(() => {
    if (inputSettingJson !== null && editMaskSetting !== null) {
      console.warn(inputSettingJson);

      const validMaskSetting = [];

      for (const mask of inputSettingJson) {
        const { start, end } = mask;

        // startとendが同じ場合は削除
        if (start === end) {
          continue;
        }

        // 数字以外の値が入っている場合は削除
        if (isNaN(start) || isNaN(end)) {
          continue;
        }

        // startとendが逆の場合は入れ替え
        validMaskSetting.push({
          start: Math.min(start, end),
          end: Math.max(start, end),
          id: crypto.randomUUID(),
        });

        // マスク設定の上限を超えた場合は削除
        if (validMaskSetting.length >= MAX_MASK_SETTING) {
          break;
        }
      }

      setMaskSettingStatus({
        event: null,
        mask: null,
      });
      setSelectedMaskSetting(null);
      setEditMaskSetting(validMaskSetting);
    }

  }, [inputSettingJson]);

  return (
    <Flex
      gap="16px"
      direction="column"
      width="100%"
      padding="0px 40px 0px 40px"
      backgroundColor="rgba(255,255,255,1)"
    >
      <Flex
        justifyContent="space-between"
        width="100%"
        padding="0px 40px 0px 60px"
        height="2rem"
      >
        {/* 操作状況表示 */}
        <Flex
          fontWeight="bold"
          color="rgba(0,0,0,1)"
          textAlign="left"
          lineHeight="24px"
          padding="0px 0px 0px 0px"
          alaignItems="center"
          fontSize="larger"
          whiteSpace="pre-wrap"
          alignItems="center"
        >
          {
            (maskSettingStatus.event == null) && (
              <Text>
                現在のマスク数 (上限: {MAX_MASK_SETTING}件) ：{editMaskSetting.length}件
              </Text>
            )
          }
          {
            maskSettingStatus.event == "new" && (
              <Text>
                新しいマスク設定を追加：{maskSettingStatus.mask.start.toFixed(3)} MHz ~ {maskSettingStatus.mask.end.toFixed(3)} MHz
              </Text>
            )
          }
          {
            (maskSettingStatus.event == "edit" && selectedMaskSetting != null) && (
              <>
                <Text>
                  選択された範囲：
                </Text>
                <TextField
                  size="small"
                  type="number"
                  step="0.001"
                  value={selectedMaskSetting.start}
                  onChange={(e) => {
                    setSelectedMaskSetting({
                      ...selectedMaskSetting,
                      start: Number(e.target.value),
                    });
                  }}
                  onBlur={() => {
                    onBlurMaskSetting();
                  }}
                />
                <Text>MHz</Text>
                <Text>~</Text>
                <TextField
                  size="small"
                  type="number"
                  step="0.001"
                  value={selectedMaskSetting.end}
                  onChange={(e) => {
                    setSelectedMaskSetting({
                      ...selectedMaskSetting,
                      end: Number(e.target.value),
                    });
                  }}
                  onBlur={() => {
                    onBlurMaskSetting();
                  }}
                />
                <Text>MHz</Text>
              </>
            )
          }
        </Flex>

        {/* ファイル読み込み */}
        {
          traceData.max.length > 0 && (
            <CommonBtnRoundWrapper
              color="Gray"
              children="traceデータを読み込む"
              onClick={() => { traceDataInputRef.current.click(); }}
            ></CommonBtnRoundWrapper>
          )
        }
      </Flex>

      {/* マスク設定 */}
      <Flex height="63vh" width="100%">
        {
          traceData.max.length > 0 ? (
            <ParentSize>
              {({ width, height }) => (
              <GraphSetting
                  location={location}
                  selectedMaskSetting={selectedMaskSetting}
                  setSelectedMaskSetting={setSelectedMaskSetting}
                  traceData={traceData}
                  width={width}
                  height={height}
                  maskSetting={maskSetting}
                  editMaskSetting={editMaskSetting}
                  setEditMaskSetting={setEditMaskSetting}
                  displaySettingJson={displaySettingJson}
                  onMaskStatusChange={onMaskStatusChange}
                  maxSettings={MAX_MASK_SETTING}
                />
              )}
            </ParentSize>
          ) : (
            <Flex
              width="100%"
              height="100%"
              justifyContent="center"
              alignItems="center"
            >
              {/* ファイル読み込み */}
              <CommonBtnRoundWrapper
                color="Gray"
                children="traceデータを読み込む"
                onClick={() => { traceDataInputRef.current.click(); }}
              ></CommonBtnRoundWrapper>
            </Flex>
          )
        }
      </Flex>

      {/* 設定変更確認モーダル */}
      <CustomModal
        open={openChangeModal}
        onClose={onCloseChangeModal}
      >
        <CheckWrapper
          msg={displaySettingJson.location_name[convertLocationName(location)] + "の干渉波マスク設定を変更します。よろしいですか？"}
          onClickOk={onClickSaveButton}
          onClickCancel={onCloseChangeModal}
        />
      </CustomModal>

      {/* traceデータを読み込む */}
      <input
        style={{ display: 'none' }}
        ref={traceDataInputRef}
        type="file"
        accept='text/csv'
        onChange={handleFileChange}
      />
    </Flex>
  );
}

export default InterferenceMonitoringMaskSettings;